import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthorizeMercadoPagoMutation, useGetBusinessByIdQuery } from '../store';
import getErrorResponse from '../utils/getErrorResponse';
import { ErrorCode } from '../utils/parseErrors';
import useQuery from '../hooks/useQuery';
import LoadingLottie from '../components/atoms/LoadingLottie';
import Button from '../components/atoms/Button';

const MPOAuthRedirectPage: React.FC = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const [code, setCode] = useState<string | null>(null);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const { data: business, isLoading: isLoadingBusiness } = useGetBusinessByIdQuery(businessId || '0', { skip: !businessId });
  const [authorizeMercadoPago, resultsAuth] = useAuthorizeMercadoPagoMutation();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [internal, setInternal] = useState<boolean>(false);

  useEffect(() => {
    if (isLoadingBusiness || resultsAuth.isLoading) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [isLoadingBusiness, resultsAuth.isLoading]);

  useEffect(() => {
    const state = query.get('state') ?? '';
    const stateParts = state.split('__');
    setCode(query.get('code') ?? null);
    setBusinessId(stateParts[0] ?? null);
    setInternal(stateParts?.[1] === 'internal');
  }, [query]);

  useEffect(() => {
    if (business?.id && code) {
      authorizeMercadoPago({ businessId: business.id, authCode: code, internal });
    }
  }, [authorizeMercadoPago, business, code, internal]);

  // Handle error states
  useEffect(() => {
    if (!code) {
      setErrorMsg('No se encontró el código o el id de la empresa');
      return;
    }

    if (!business && !isLoadingBusiness && businessId) {
      setErrorMsg('No se encontró la productora');
      return;
    }

    if (resultsAuth.isError) {
      const error = getErrorResponse(resultsAuth.error);
      if (error.code === ErrorCode.MercadoPagoAlreadyAuthorized) {
        setErrorMsg('MercadoPago ya autorizado');
      } else {
        setErrorMsg('Error al autorizar');
      }
    }
  }, [code, business, isLoadingBusiness, businessId, resultsAuth.isError, resultsAuth.error]);

  return (
    <div className="my-6 min-h-[80vh] glass-surface">
      <h1 className="text-3xl text-center">Autorización de Mercado Pago {internal ? 'CUENTA INTERNA AVANTTI' : ''}</h1>
      <div className="flex justify-center my-8">
        <img src="/logo_mp_hor.png" className="w-80 glass-surface bg-white/20 p-1" alt="Mercado Pago logo" />
      </div>
      {isLoading && <LoadingLottie minHeight='-' />}
      {!isLoading && (
        <div className="flex justify-center items-center text-xl my-5">
          {resultsAuth.isSuccess && <div className="text-success-600 text-2xl">Integración exitosa</div>}
          {resultsAuth.isError && <div className="text-error-600 text-2xl">{errorMsg}</div>}
        </div>
      )}
      <div className="flex justify-center my-8 w-fit items-center mx-auto">
        <Button variant='primary' onClick={() => navigate('/admin')}>Volver al panel de control</Button>
      </div>
    </div>
  );
};

export default MPOAuthRedirectPage;
